// Estado de "qué pestaña del panel de administración está activa", compartido entre el Sidebar
// (que dibuja el flyout de navegación, ver AdminFlyout) y AdminView (que dibuja el contenido, ver
// src/views/AdminView/index.tsx). Antes vivía como useState local dentro de AdminView porque su
// propio <aside> convivía con el contenido en el mismo componente; al mover la navegación al
// Sidebar (fuera del árbol de AdminView) hace falta un punto compartido — Context, no una librería
// de estado nueva, por la convención del proyecto (ver CLAUDE.md: "Estado global UI: React
// Context").
//
// Ver docs/superpowers/specs/2026-08-04-menu-unificado-design.md, Fase 3.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AdminSection, RecognitionAdminSection } from '@/auth/accessMatrix';

const FUND_PREFIX = 'fund:';
export type FundTab = `${typeof FUND_PREFIX}${string}`;
export type PanelTab = AdminSection | FundTab;

export const fundTab = (slug: RecognitionAdminSection | string): FundTab => `${FUND_PREFIX}${slug}`;
export const isFundTab = (tab: PanelTab): tab is FundTab => tab.startsWith(FUND_PREFIX);
export const fundSlug = (tab: FundTab): string => tab.slice(FUND_PREFIX.length);

const STORAGE_KEY = 'admin-activeTab';
const DEFAULT_TAB: PanelTab = 'surveys';

function readStoredTab(): PanelTab | null {
  try {
    return (localStorage.getItem(STORAGE_KEY) as PanelTab) ?? null;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('AdminPanelContext: no se pudo leer localStorage', error);
    }
    return null;
  }
}

interface AdminPanelContextValue {
  activeTab: PanelTab;
  setActiveTab: (tab: PanelTab) => void;
  /** Visibilidad del flyout de navegación del admin (AdminFlyout). Estado transitorio de UI —
   *  a diferencia de `activeTab`, NO se persiste en localStorage: no tiene sentido que el flyout
   *  "recuerde" haber estado abierto entre sesiones. Compartido entre Sidebar (botón de escritorio
   *  + deep-link) y el nav inferior móvil (MobileNav.tsx), ver fix de huecos de Task 12. */
  isAdminFlyoutOpen: boolean;
  setIsAdminFlyoutOpen: (open: boolean) => void;
}

const AdminPanelContext = createContext<AdminPanelContextValue | null>(null);

export function AdminPanelProvider({ children }: { children: ReactNode }) {
  const [activeTab, setActiveTabState] = useState<PanelTab>(() => readStoredTab() ?? DEFAULT_TAB);
  const [isAdminFlyoutOpen, setIsAdminFlyoutOpenState] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, activeTab);
    } catch (error) {
      // Almacenamiento no disponible (modo privado, cuota excedida, etc.): la pestaña activa no
      // sobrevive un refresh, pero la app sigue funcionando con el estado en memoria.
      if (import.meta.env.DEV) {
        console.error('AdminPanelContext: no se pudo persistir la pestaña activa', error);
      }
    }
  }, [activeTab]);

  // `useCallback` (identidad estable) + `useMemo` sobre el `value` del Provider (Fase 3, fix de
  // revisión de Task 11 — B-1): sin esto, cada render del Provider creaba una función
  // `setActiveTab` y un objeto `value` NUEVOS, así que cualquier consumidor con un efecto que
  // dependiera de `setActiveTab` (p. ej. el deep-link de AdminView) volvía a correr en CADA cambio
  // de `activeTab` — no solo cuando sus propias dependencias cambiaban. En AdminView eso
  // reimponía la sección de la URL cada vez que el usuario cambiaba de pestaña desde el flyout,
  // deshaciendo el clic.
  const setActiveTab = useCallback((tab: PanelTab): void => {
    setActiveTabState(tab);
  }, []);

  // Misma identidad estable que `setActiveTab` (ver comentario B-1 arriba): el deep-link de
  // Sidebar depende de `setIsAdminFlyoutOpen` en un `useEffect`, así que una identidad nueva en
  // cada render volvería a dispararlo sin que `location.pathname` haya cambiado.
  const setIsAdminFlyoutOpen = useCallback((open: boolean): void => {
    setIsAdminFlyoutOpenState(open);
  }, []);

  const value = useMemo<AdminPanelContextValue>(
    () => ({ activeTab, setActiveTab, isAdminFlyoutOpen, setIsAdminFlyoutOpen }),
    [activeTab, setActiveTab, isAdminFlyoutOpen, setIsAdminFlyoutOpen],
  );

  return (
    <AdminPanelContext.Provider value={value}>
      {children}
    </AdminPanelContext.Provider>
  );
}

export function useAdminPanelContext(): AdminPanelContextValue {
  const ctx = useContext(AdminPanelContext);
  if (!ctx) {
    throw new Error('useAdminPanelContext must be used within <AdminPanelProvider>');
  }
  return ctx;
}
